import { isEmail } from 'class-validator';
import { isNil } from 'lodash';

import { CustomFieldType, isPhoneValid, t, toFiniteNumberOrNull } from '@nova/core';

type ValidationRule = (value: unknown) => boolean | string;

/** Options for creating custom field validation rules (third argument to createCustomFieldRules). */
export interface CreateCustomFieldRulesOptions {
  required: boolean;
  minLengthOrValue?: number | null;
  maxLengthOrValue?: number | null;
}

/**
 * Same keys, and — for required/email/phone — the same underlying check
 * (isEmail from class-validator, isPhoneValid from @nova/core) as
 * Validator.vue's rules.customField(), which Helios registers as $validator.
 * That plugin is satellite code but only installed globally by helios/luna, so
 * these rules are reproduced directly rather than depending on it.
 */
export const messages = {
  required: (fieldLabel: string) =>
    fieldLabel
      ? `${fieldLabel} ${t('global.basicTerms.required')}`
      : t('global.errors.thisFieldIsRequired'),
  invalidEmail: () => t('global.errors.emailMustBeValid'),
  invalidPhone: () => t('global.errors.pleaseEnterAValidPhoneNumber'),
  minLength: (min: number) => t('global.errors.mustBeAtLeastXCharacters', { min }),
  maxLength: (max: number) => t('global.errors.mustBeAtMostXCharacters', { max }),
  minValue: (min: number) => t('global.errors.mustBeNumberGreaterThan', { min }),
  maxValue: (max: number) => t('global.errors.mustBeNumberLessThan', { max })
};

/**
 * This is a pure function that returns Vuetify-compatible validation rules.
 * Used by the useCustomFieldValidation composable and can be used directly
 * where needed.
 *
 * @param fieldLabel - The field label for error messages
 * @param type - The custom field type (from CustomFieldType enum)
 * @param options - Object containing required flag and optional min/max constraints
 * @returns Array of validation functions compatible with Vuetify :rules prop
 */
export function createCustomFieldRules(
  fieldLabel: string,
  type: CustomFieldType,
  options: CreateCustomFieldRulesOptions
): Array<ValidationRule> {
  const { required } = options;

  // Normalize min/max to a finite number or null ("no constraint"). Legacy Helios data can
  // persist '' (empty string) for blank min/max length inputs; '' must not be treated as a
  // real constraint, otherwise e.g. value.length <= '' coerces to <= 0 and every value fails.
  // (OD-3771)
  const minLengthOrValue = toFiniteNumberOrNull(options.minLengthOrValue);
  const maxLengthOrValue = toFiniteNumberOrNull(options.maxLengthOrValue);

  const rules: Array<ValidationRule> = [];

  // Required validation — Vuetify expects each rule to return true (boolean) or a string.
  if (required) {
    const requiredMsg = messages.required(fieldLabel);
    rules.push((v: unknown): boolean | string => {
      if (Array.isArray(v)) {
        return v.length > 0 ? true : requiredMsg;
      }
      if (typeof v === 'boolean') {
        return true;
      }
      if (typeof v === 'number') {
        return Number.isFinite(v) ? true : requiredMsg;
      }
      return v ? true : requiredMsg;
    });
  }

  // Type-specific validations
  if (type === CustomFieldType.Email) {
    rules.push((v: unknown): boolean | string => {
      if (!v) {
        return true;
      }
      return typeof v === 'string' && isEmail(v) ? true : messages.invalidEmail();
    });
  }

  if (type === CustomFieldType.Phone) {
    rules.push((v: unknown): boolean | string => {
      if (!v) {
        return true;
      }
      return typeof v === 'string' && isPhoneValid(v) ? true : messages.invalidPhone();
    });
  }

  // Length/range validations. min/max are already normalized to a finite number or null
  // above, so any non-null value here is a real constraint.
  if (!isNil(minLengthOrValue) || !isNil(maxLengthOrValue)) {
    if (type === CustomFieldType.String || type === CustomFieldType.BigString) {
      if (!isNil(minLengthOrValue)) {
        const minLen = minLengthOrValue;
        const minLenMsg = messages.minLength(minLen);
        rules.push((v: unknown): boolean | string => {
          if (!v) {
            return true;
          }
          return typeof v === 'string' && v.length >= minLen ? true : minLenMsg;
        });
      }
      if (!isNil(maxLengthOrValue)) {
        const maxLen = maxLengthOrValue;
        const maxLenMsg = messages.maxLength(maxLen);
        rules.push((v: unknown): boolean | string => {
          if (!v) {
            return true;
          }
          return typeof v === 'string' && v.length <= maxLen ? true : maxLenMsg;
        });
      }
    } else if (type === CustomFieldType.Number) {
      if (!isNil(minLengthOrValue)) {
        const minVal = minLengthOrValue;
        const minValMsg = messages.minValue(minVal);
        rules.push((v: unknown): boolean | string => {
          if (v === '' || isNil(v)) {
            return true;
          }
          return Number(v) >= minVal ? true : minValMsg;
        });
      }
      if (!isNil(maxLengthOrValue)) {
        const maxVal = maxLengthOrValue;
        const maxValMsg = messages.maxValue(maxVal);
        rules.push((v: unknown): boolean | string => {
          if (v === '' || isNil(v)) {
            return true;
          }
          return Number(v) <= maxVal ? true : maxValMsg;
        });
      }
    }
  }

  return rules;
}
